import { useEffect, useMemo, useRef, useState } from "react";
import Game from "./Game.jsx";

async function apiGet(path) {
  const r = await fetch(path, { credentials: "include" });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}
async function apiPost(path, body) {
  const r = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body ?? {}),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

function useJuice() {
  const [shake, setShake] = useState(0);
  const [hitStop, setHitStop] = useState(false);
  const raf = useRef(null);
  const doShake = (strength = 6, ms = 260) => {
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / ms);
      setShake((1 - p) * strength);
      if (p < 1) raf.current = requestAnimationFrame(tick);
      else setShake(0);
    };
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(tick);
  };
  const doHitStop = (ms = 90) => { setHitStop(true); setTimeout(() => setHitStop(false), ms); };
  return { shake, hitStop, doShake, doHitStop };
}

export default function App() {
  const { shake, hitStop, doShake, doHitStop } = useJuice();
  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    (async () => {
      try { setLoadingMe(true); const d = await apiGet("/api/me"); setMe(d.user); }
      catch { setMe(null); }
      finally { setLoadingMe(false); }
    })();
  }, []);

  async function beginGoogleLogin() {
    try {
      doHitStop(80);
      const { url } = await apiPost("/api/auth/google/start", { returnTo: window.location.origin });
      window.location.href = url;
    } catch (e) { setBanner({ type: "err", msg: String(e?.message ?? e) }); }
  }

  async function logout() {
    try { await apiPost("/api/logout"); setMe(null); doShake(8, 220); setBanner({ type: "ok", msg: "Logged out." }); }
    catch (e) { setBanner({ type: "err", msg: String(e?.message ?? e) }); }
  }

  async function syncProfile(payload) {
    const out = await apiPost("/api/profile/upsert", payload);
    setMe(out.user);
  }

  const shakeStyle = useMemo(() => {
    if (!shake) return {};
    const dx = (Math.random() - 0.5) * shake;
    const dy = (Math.random() - 0.5) * shake;
    return { transform: `translate(${dx}px, ${dy}px)` };
  }, [shake]);

  return (
    <div className={hitStop ? "hitstop" : ""} style={shakeStyle}>
      <TopBar me={me} loading={loadingMe} onLogin={beginGoogleLogin} onLogout={logout} />
      {banner ? <div className={banner.type === "err" ? "toperr" : "topok"}>{banner.msg}</div> : null}
      <Game
        me={me}
        onNeedLogin={() => setBanner({ type: "err", msg: "Sign in with Google to sync to the leaderboard." })}
        onSyncProfile={syncProfile}
        onJuice={{ shake: doShake, hitStop: doHitStop }}
      />
      <Style />
    </div>
  );
}

function TopBar({ me, loading, onLogin, onLogout }) {
  return (
    <header className="authbar">
      <div className="authbrand">
        <div style={{ fontSize: 26 }}>🐉</div>
        <div>
          <div className="authtitle">RASMUSTEN</div>
          <div className="authsub">Google login + leaderboard</div>
        </div>
      </div>
      <div className="authactions">
        {loading ? (
          <span className="muted">Checking session…</span>
        ) : me ? (
          <>
            <span className="pill">{me.email ?? "Google User"}</span>
            {me.characterName ? <span className="pill gold">{me.characterName}</span> : null}
            <button className="btn" onClick={onLogout}>Logout</button>
          </>
        ) : (
          <button className="btn primary" onClick={onLogin}>Sign in with Google</button>
        )}
      </div>
    </header>
  );
}

function Style() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;700;900&family=MedievalSharp&family=Spectral:wght@400;500;600&display=swap');
      html,body{margin:0;padding:0;background:#0a0805;}
      .hitstop{filter:saturate(1.12) contrast(1.02)}
      .authbar{font-family:Spectral,serif;position:sticky;top:0;z-index:60;display:flex;justify-content:space-between;gap:12px;align-items:center;padding:10px 16px;border-bottom:1px solid #3a2f1a;background:rgba(10,8,4,0.7);backdrop-filter:blur(10px)}
      .authbrand{display:flex;align-items:center;gap:10px}
      .authtitle{font-family:Cinzel,serif;font-weight:900;letter-spacing:2px;color:#ffd76b}
      .authsub{font-family:MedievalSharp,cursive;color:#bfa971;font-size:12px}
      .authactions{display:flex;align-items:center;gap:8px;flex-wrap:wrap;justify-content:flex-end}
      .pill{border:1px solid #5a4a2a;border-radius:999px;padding:6px 10px;background:rgba(0,0,0,0.35);font-size:12px;color:#ffe9c0}
      .pill.gold{border-color:#a86b30;color:#ffd76b}
      .muted{color:#a89770}
      .btn{cursor:pointer;border-radius:10px;border:1px solid #5a4a2a;background:rgba(0,0,0,0.35);color:#ffe9c0;padding:8px 12px;font-family:Cinzel,serif;font-weight:800;box-shadow:0 3px 0 rgba(0,0,0,0.35)}
      .btn.primary{background:linear-gradient(180deg,#6b3f1a,#4a2a10);border-color:#a86b30}
      .toperr{max-width:980px;margin:10px auto 0;padding:10px 12px;border-radius:12px;border:1px solid #7a3a3a;background:rgba(80,20,20,0.3);color:#ffb3b3}
      .topok{max-width:980px;margin:10px auto 0;padding:10px 12px;border-radius:12px;border:1px solid #3a7a3a;background:rgba(20,80,20,0.3);color:#b3ffb3}
    `}</style>
  );
}
